import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Input,
  Button,
  Select,
  Icon,
  Spinner,
  Alert,
  Badge,
  Link,
} from '@chakra-ui/react'
import { FiSearch, FiFilter, FiRefreshCw, FiAlertCircle } from 'react-icons/fi'
import { FaFacebook, FaGoogle } from 'react-icons/fa'
import { apiConfig } from '../../config/api'
import { DataTable, Pagination } from '../../components/admin/DataTable'
import { StatusBadge } from '../../components/admin/StatusBadge'

interface Campaign {
  id: string
  practitioner_id: string
  practitioner_name?: string
  practitioner_email?: string
  status: string
  impressions_total: number
  impressions_delivered: number
  selected_platforms?: string[]
  meta_status?: string
  meta_error?: string
  google_status?: string
  google_error?: string
  created_at?: string
  updated_at?: string
}

export const CampaignsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || ''
  )
  const [metaErrorOnly, setMetaErrorOnly] = useState<boolean>(
    searchParams.get('metaErrorOnly') === 'true'
  )
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  const fetchCampaigns = async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (metaErrorOnly) params.append('metaErrorOnly', 'true')
      params.append('limit', pageSize.toString())
      params.append('offset', ((currentPage - 1) * pageSize).toString())

      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/campaigns?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load campaigns')
      }

      const result = await response.json()
      setCampaigns(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load campaigns')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCampaigns()
  }, [statusFilter, metaErrorOnly, currentPage])

  useEffect(() => {
    const params = new URLSearchParams()
    if (statusFilter) params.set('status', statusFilter)
    if (metaErrorOnly) params.set('metaErrorOnly', 'true')
    setSearchParams(params, { replace: true })
  }, [statusFilter, metaErrorOnly, setSearchParams])

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      campaign.id.toLowerCase().includes(searchLower) ||
      campaign.practitioner_name?.toLowerCase().includes(searchLower) ||
      campaign.practitioner_email?.toLowerCase().includes(searchLower)
    )
  })

  const columns = [
    {
      key: 'id',
      label: 'Campaign ID',
      render: (campaign: Campaign) => (
        <Link
          color="blue.500"
          onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
          cursor="pointer"
        >
          {campaign.id.substring(0, 12)}...
        </Link>
      ),
    },
    {
      key: 'practitioner_name',
      label: 'Practitioner',
      render: (campaign: Campaign) => (
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">
            {campaign.practitioner_name || 'Unknown'}
          </Text>
          {campaign.practitioner_email && (
            <Text fontSize="xs" color="gray.500">
              {campaign.practitioner_email}
            </Text>
          )}
        </VStack>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (campaign: Campaign) => <StatusBadge status={campaign.status} />,
    },
    {
      key: 'impressions',
      label: 'Impressions',
      render: (campaign: Campaign) => (
        <Text>
          {campaign.impressions_delivered.toLocaleString()} /{' '}
          {campaign.impressions_total.toLocaleString()}
        </Text>
      ),
    },
    {
      key: 'platform_status',
      label: 'Platform Status',
      render: (campaign: Campaign) => (
        <VStack align="start" gap={1}>
          {/* Meta Status */}
          {(campaign.selected_platforms?.includes('meta') || campaign.meta_status) && (
            <HStack gap={2}>
              <Icon as={FaFacebook} color="blue.600" boxSize={3} />
              {campaign.meta_error ? (
                <Badge colorPalette="red" size="sm">
                  <HStack gap={1}>
                    <Icon as={FiAlertCircle} boxSize={3} />
                    <Text>Error</Text>
                  </HStack>
                </Badge>
              ) : campaign.meta_status ? (
                <StatusBadge status={campaign.meta_status} />
              ) : (
                <Text color="gray.400" fontSize="xs">-</Text>
              )}
            </HStack>
          )}
          {/* Google Status */}
          {(campaign.selected_platforms?.includes('google') || campaign.google_status) && (
            <HStack gap={2}>
              <Icon as={FaGoogle} color="red.500" boxSize={3} />
              {campaign.google_error ? (
                <Badge colorPalette="red" size="sm">
                  <HStack gap={1}>
                    <Icon as={FiAlertCircle} boxSize={3} />
                    <Text>Error</Text>
                  </HStack>
                </Badge>
              ) : campaign.google_status ? (
                <StatusBadge status={campaign.google_status} />
              ) : (
                <Text color="gray.400" fontSize="xs">-</Text>
              )}
            </HStack>
          )}
          {/* No platforms selected - show dash */}
          {!campaign.selected_platforms?.length && !campaign.meta_status && !campaign.google_status && (
            <Text color="gray.400">-</Text>
          )}
        </VStack>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (campaign: Campaign) => {
        if (!campaign.created_at) return <Text color="gray.400">-</Text>
        const date = new Date(campaign.created_at)
        return <Text fontSize="sm">{date.toLocaleDateString()}</Text>
      },
    },
  ]

  return (
    <Container maxW="7xl">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="end">
          <VStack align="start" gap={2}>
            <Heading size="2xl" color="gray.900">
              Campaigns
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Manage and monitor all ad campaigns
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={fetchCampaigns}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Filters */}
        <Card.Root>
          <Card.Body p={4}>
            <HStack gap={4} flexWrap="wrap">
              <HStack flex={1} minW="200px">
                <Icon as={FiSearch} color="gray.400" />
                <Input
                  placeholder="Search by ID, name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </HStack>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="All Statuses"
                w="200px"
              >
                <option value="draft">Draft</option>
                <option value="pending_payment">Pending Payment</option>
                <option value="creative_review">Creative Review</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </Select>
              <Button
                variant={metaErrorOnly ? 'solid' : 'outline'}
                colorPalette={metaErrorOnly ? 'red' : 'gray'}
                leftIcon={<Icon as={FiFilter} />}
                onClick={() => {
                  setMetaErrorOnly(!metaErrorOnly)
                  setCurrentPage(1)
                }}
              >
                Meta Errors Only
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>

        {/* Error Alert */}
        {error && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              {error}
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Campaigns Table */}
        <Card.Root>
          <Card.Body p={0}>
            <DataTable
              data={filteredCampaigns}
              columns={columns}
              onRowClick={(campaign) =>
                navigate(`/admin/campaigns/${campaign.id}`)
              }
              isLoading={isLoading}
              emptyMessage="No campaigns found"
            />
            {!isLoading && filteredCampaigns.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredCampaigns.length / pageSize)}
                onPageChange={setCurrentPage}
              />
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  )
}

